import type { MaybeRefOrGetter } from 'vue';
import type { Filter, ResolvedMonospaceApiKey } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { isPastDateTime } from '#layers/base/public';
import { isRelationalFilter, usePaginatedListing } from '#layers/data-model/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { showConfirmationDialog, useToastResultHandling } from '#layers/ui/public';

const API_KEY_COLLECTION = 'MonospaceApiKey';

interface UseApiKeysProps {
	// If no filter is provided, no query will be executed
	// But we can still create/deactivate/reactivate/revoke api keys
	filter?: MaybeRefOrGetter<Filter>;
}

export function useApiKeys(props?: MaybeRefOrGetter<UseApiKeysProps>) {
	const { t } = useI18n();
	const store = useSystemEntityStore();

	const filter = computed(() => toValue(props)?.filter ?? {});
	const hasFilter = computed(() => Boolean(toValue(props)?.filter));
	const fetchPolicy = computed(() => isRelationalFilter(filter.value) ? 'no-cache' : 'cache-and-fetch');

	const {
		items: apiKeys,
		pageNumber,
		pageSize,
		totalItemsCount,
		currentItemsCount,
		isLoading,
	} = usePaginatedListing<ResolvedMonospaceApiKey>({
		store,
		collectionApiName: API_KEY_COLLECTION,
		enabled: hasFilter,
		resetOn: filter,
		// TODO: Refactor useItems to use base composable that works independent of Resolved schema
		// https://linear.app/directus/issue/NEXT-1929/refactor-useitems-to-use-base-composable-that-works-independent-of
		createQuery: (queryPageSize) => store.MonospaceApiKey.query((q) => q.many({
			filter: filter.value,
			enabled: hasFilter.value,
			include: { user: {}, creator: {} },
			fetchPolicy: fetchPolicy.value,
			resultMode: 'responseRefs',
			sort: [
				{ active: { direction: 'asc' } },
				{ expiresAt: { direction: 'asc' } },
				{ id: { direction: 'asc' } },
			],
			pageIndex: 0,
			pageSize: queryPageSize.value,
		})),
	});

	const { onSuccess: deactivateSuccess, onError: deactivateError } = useToastResultHandling({
		success: {
			title: t('api_keys.deactivate_success'),
		},
		error: {
			title: t('api_keys.deactivate_error'),
		},
	});

	const { onSuccess: activateSuccess, onError: activateError } = useToastResultHandling({
		success: {
			title: t('api_keys.reactivate_success'),
		},
		error: {
			title: t('api_keys.reactivate_error'),
		},
	});

	const { onSuccess: revokeSuccess, onError: revokeError } = useToastResultHandling({
		success: {
			title: t('api_keys.revoke_success'),
		},
		error: {
			title: t('api_keys.revoke_error'),
		},
	});

	async function deactivate(apiKey: ResolvedMonospaceApiKey) {
		try {
			await store.MonospaceApiKey.update({ active: false }, { key: apiKey.id });
			deactivateSuccess();
		}
		catch (error) {
			deactivateError(error);
		}
	}

	async function reactivate(apiKey: ResolvedMonospaceApiKey) {
		try {
			await store.MonospaceApiKey.update({ active: true }, { key: apiKey.id });
			activateSuccess();
		}
		catch (error) {
			activateError(error);
		}
	}

	async function revoke(apiKey: ResolvedMonospaceApiKey) {
		const confirmed = await showConfirmationDialog({
			props: {
				title: t('api_keys.revoke_confirm.title'),
				description: t('api_keys.revoke_confirm.description'),
				confirm: {
					color: 'error',
					label: `${t('api_keys.revoke')}`,
				},
			},
		});

		if (!confirmed) return;

		try {
			await store.MonospaceApiKey.delete(apiKey.id);
			revokeSuccess();
		}
		catch (error) {
			revokeError(error);
		}
	}

	return {
		apiKeys,
		pageNumber,
		pageSize,
		totalItemsCount,
		currentItemsCount,
		isLoading,
		deactivate,
		reactivate,
		revoke,
		isExpired,
		isDisabled,
	};
}

function isExpired(apiKey: ResolvedMonospaceApiKey): boolean {
	if (!apiKey.expiresAt) return false;
	return isPastDateTime(apiKey.expiresAt);
}

function isDisabled(apiKey: ResolvedMonospaceApiKey): boolean {
	return !apiKey.active || isExpired(apiKey);
}
